/**
 * YAML Converter (`json-yaml.js`)
 *
 * `toYaml(value)` emits; `fromYaml(text)` parses a documented subset.
 *
 * **The emitter's real job is quoting.** YAML 1.1 resolves a startling number of
 * bare words to non-strings: `no` and `off` are booleans, `~` is null, `0755` is
 * an integer, `1:30` is sexagesimal. A converter that writes `country: no` has
 * silently turned Norway into `false` — the "Norway problem", and the reason
 * `needsQuoting()` below is the most carefully tested function in this file.
 *
 * **The parser's real job is refusing.** It supports block mappings and
 * sequences, flow collections, the three scalar styles, `|` and `>` block
 * scalars with chomping, comments and a leading `---`. It does *not* support
 * anchors and aliases (`&a` / `*a`), merge keys (`<<`), explicit tags (`!!str`),
 * multi-document streams, or complex `? ` keys — and when it meets one it names
 * the construct and the line rather than guessing. Silently misreading someone's
 * config is the worst outcome available to this module.
 *
 * Pure vanilla ES module. Zero dependencies.
 */

/** Bare words YAML 1.1 resolves to a boolean or null. */
const BOOL_OR_NULL = /^(y|yes|n|no|true|false|on|off|null|~)$/i;

/**
 * Bare words YAML resolves to a number — including leading-zero integers and
 * sexagesimals like `1:30`, both of which look like strings to everyone else.
 */
const NUMBER_LIKE =
  /^[-+]?(\d[\d_]*(\.[\d_]*)?([eE][-+]?\d+)?|\.\d+([eE][-+]?\d+)?|0o[0-7]+|0x[0-9a-fA-F]+|\d+(:\d+)+(\.\d+)?|\.(inf|nan))$/i;

/** Characters that mean something to YAML when they lead a scalar. */
const LEADING_INDICATORS = "-?:,[]{}#&*!|>'\"%@`";

/** Control characters that force a quoted scalar. */
const CONTROL_CHARS = /[ --]/;

class YamlError extends Error {
  constructor(message, line) {
    super(line ? `${message} (line ${line})` : message);
    this.name = "YamlError";
    this.line = line ?? null;
  }
}

/* -------------------------------------------------------------------------
   Emit
   ------------------------------------------------------------------------- */

function needsQuoting(text) {
  if (text === "") return true;
  if (BOOL_OR_NULL.test(text)) return true;
  if (NUMBER_LIKE.test(text)) return true;
  if (/^\s|\s$/.test(text)) return true;
  if (LEADING_INDICATORS.includes(text[0])) return true;
  if (text.includes(": ") || text.endsWith(":")) return true;
  if (text.includes(" #")) return true;
  if (CONTROL_CHARS.test(text)) return true;
  return false;
}

function quoteScalar(text) {
  return JSON.stringify(text);
}

/** A literal block scalar is only safe when no line carries trailing space. */
function canUseBlockScalar(text) {
  if (!text.includes("\n")) return false;
  if (/^\s/.test(text)) return false;
  if (CONTROL_CHARS.test(text.replace(/\n/g, ""))) return false;
  const body = text.endsWith("\n") ? text.slice(0, -1) : text;
  if (body.endsWith("\n")) return false;
  return body.split("\n").every((line) => line === "" || !/\s$/.test(line));
}

function emitScalar(value) {
  if (value === null) return "null";
  if (typeof value === "boolean") return String(value);
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "null";
  const text = String(value);
  return needsQuoting(text) ? quoteScalar(text) : text;
}

function isEmptyContainer(value) {
  return Array.isArray(value) ? value.length === 0 : Object.keys(value).length === 0;
}

function emitNode(value, indent, lines) {
  const pad = "  ".repeat(indent);

  if (Array.isArray(value)) {
    for (const item of value) {
      // A container element hoists its first line onto the dash, so a list of
      // mappings reads `- name: Ada` the way it is written by hand rather than
      // stranding the dash on a line of its own.
      if (item !== null && typeof item === "object" && !isEmptyContainer(item)) {
        const sub = [""];
        emitNode(item, indent + 1, sub);
        const body = sub.slice(1);
        const childPad = "  ".repeat(indent + 1);
        lines.push(`${pad}- ${body[0].slice(childPad.length)}`);
        for (let k = 1; k < body.length; k += 1) lines.push(body[k]);
        continue;
      }
      lines.push(`${pad}-`);
      emitInline(item, indent + 1, lines);
    }
    return;
  }

  for (const key of Object.keys(value)) {
    const label = needsQuoting(key) ? quoteScalar(key) : key;
    lines.push(`${pad}${label}:`);
    emitInline(value[key], indent + 1, lines);
  }
}

/** Append a value to the line just pushed, expanding to a block when needed. */
function emitInline(value, indent, lines) {
  if (value !== null && typeof value === "object") {
    if (isEmptyContainer(value)) {
      lines[lines.length - 1] += Array.isArray(value) ? " []" : " {}";
      return;
    }
    emitNode(value, indent, lines);
    return;
  }
  if (typeof value === "string" && canUseBlockScalar(value)) {
    const pad = "  ".repeat(indent);
    const chomp = value.endsWith("\n") ? "" : "-";
    lines[lines.length - 1] += ` |${chomp}`;
    const body = value.endsWith("\n") ? value.slice(0, -1) : value;
    for (const line of body.split("\n")) lines.push(line === "" ? "" : pad + line);
    return;
  }
  lines[lines.length - 1] += ` ${emitScalar(value)}`;
}

/**
 * Serialise a JSON value as block-style YAML.
 * Key order is the document's own; nothing is sorted or reordered.
 */
export function toYaml(value) {
  if (value === null || typeof value !== "object") return `${emitScalar(value)}\n`;
  if (isEmptyContainer(value)) return Array.isArray(value) ? "[]\n" : "{}\n";
  // A sentinel line so the top level can use the same "append to previous" path.
  const lines = [""];
  emitNode(value, 0, lines);
  return `${lines.slice(1).join("\n")}\n`;
}

/* -------------------------------------------------------------------------
   Parse
   ------------------------------------------------------------------------- */

/** Strip a trailing `#` comment, respecting quotes. */
function stripComment(line) {
  let quote = null;
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i];
    if (quote) {
      if (c === "\\" && quote === '"') i += 1;
      else if (c === quote) quote = null;
      continue;
    }
    if (c === '"' || c === "'") {
      quote = c;
      continue;
    }
    if (c === "#" && (i === 0 || /\s/.test(line[i - 1]))) return line.slice(0, i);
  }
  return line;
}

function indentOf(line) {
  let n = 0;
  while (n < line.length && line[n] === " ") n += 1;
  return n;
}

function unescapeDouble(c) {
  const map = { n: "\n", t: "\t", r: "\r", b: "\b", f: "\f", '"': '"', "\\": "\\", "/": "/" };
  return Object.prototype.hasOwnProperty.call(map, c) ? map[c] : c;
}

function resolvePlain(text, lineNo) {
  if (text === "" || text === "~" || /^null$/i.test(text)) return null;
  if (/^(true|yes|on)$/i.test(text)) return true;
  if (/^(false|no|off)$/i.test(text)) return false;
  if (text[0] === "&" || text[0] === "*") {
    throw new YamlError(`Anchors and aliases are not supported (${text.slice(0, 12)})`, lineNo);
  }
  if (text.startsWith("!")) throw new YamlError(`Explicit tags are not supported (${text.slice(0, 12)})`, lineNo);
  if (/^[-+]?\d[\d_]*$/.test(text)) return Number(text.replace(/_/g, ""));
  if (/^[-+]?(\d[\d_]*)?\.\d[\d_]*([eE][-+]?\d+)?$/.test(text) || /^[-+]?\d[\d_]*[eE][-+]?\d+$/.test(text)) {
    return Number(text.replace(/_/g, ""));
  }
  if (/^0x[0-9a-fA-F]+$/.test(text)) return parseInt(text, 16);
  if (/^0o[0-7]+$/.test(text)) return parseInt(text.slice(2), 8);
  if (/^[-+]?\.inf$/i.test(text) || /^\.nan$/i.test(text)) {
    throw new YamlError("JSON cannot represent .inf or .nan", lineNo);
  }
  return text;
}

function parseFlow(src, lineNo) {
  let i = 0;
  const fail = (message) => {
    throw new YamlError(message, lineNo);
  };
  const ws = () => {
    while (i < src.length && /\s/.test(src[i])) i += 1;
  };

  function parseValue() {
    ws();
    const c = src[i];
    if (c === "[") {
      i += 1;
      const out = [];
      ws();
      if (src[i] === "]") {
        i += 1;
        return out;
      }
      for (;;) {
        out.push(parseValue());
        ws();
        if (src[i] === ",") {
          i += 1;
          ws();
          if (src[i] === "]") {
            i += 1;
            return out;
          }
          continue;
        }
        if (src[i] === "]") {
          i += 1;
          return out;
        }
        fail("Expected ',' or ']' in a flow sequence");
      }
    }
    if (c === "{") {
      i += 1;
      const out = {};
      ws();
      if (src[i] === "}") {
        i += 1;
        return out;
      }
      for (;;) {
        ws();
        const key = parseValue();
        ws();
        if (src[i] !== ":") fail("Expected ':' in a flow mapping");
        i += 1;
        out[String(key)] = parseValue();
        ws();
        if (src[i] === ",") {
          i += 1;
          ws();
          if (src[i] === "}") {
            i += 1;
            return out;
          }
          continue;
        }
        if (src[i] === "}") {
          i += 1;
          return out;
        }
        fail("Expected ',' or '}' in a flow mapping");
      }
    }
    if (c === '"' || c === "'") {
      const quote = c;
      i += 1;
      let out = "";
      while (i < src.length && src[i] !== quote) {
        if (quote === '"' && src[i] === "\\") {
          out += unescapeDouble(src[i + 1]);
          i += 2;
          continue;
        }
        if (quote === "'" && src[i] === "'" && src[i + 1] === "'") {
          out += "'";
          i += 2;
          continue;
        }
        out += src[i];
        i += 1;
      }
      if (src[i] !== quote) fail("Unterminated quoted scalar");
      i += 1;
      return out;
    }
    // `:` terminates a plain scalar in flow context — without it, the key of
    // `{b: 'c'}` swallows its own value.
    const start = i;
    while (i < src.length && !",[]{}:".includes(src[i])) i += 1;
    return resolvePlain(src.slice(start, i).trim(), lineNo);
  }

  const value = parseValue();
  ws();
  if (i < src.length) fail(`Unexpected ${JSON.stringify(src[i])} after a flow collection`);
  return value;
}

/** Parse a scalar that appears after `key:` or `- `. */
function parseScalarText(text, lineNo) {
  const trimmed = text.trim();
  const first = trimmed[0];
  if (first === "[" || first === "{" || first === '"' || first === "'") return parseFlow(trimmed, lineNo);
  return resolvePlain(trimmed, lineNo);
}

/**
 * Locate the `:` that ends a mapping key: outside quotes and flow markers, and
 * followed by whitespace or end of line.
 */
function findKeyColon(content) {
  let quote = null;
  let depth = 0;
  for (let i = 0; i < content.length; i += 1) {
    const c = content[i];
    if (quote) {
      if (quote === '"' && c === "\\") i += 1;
      else if (c === quote) quote = null;
      continue;
    }
    if (c === '"' || c === "'") {
      quote = c;
      continue;
    }
    if (c === "[" || c === "{") depth += 1;
    else if (c === "]" || c === "}") depth -= 1;
    else if (c === ":" && depth === 0 && (i + 1 === content.length || /\s/.test(content[i + 1]))) return i;
  }
  return -1;
}

function decodeKey(raw, lineNo) {
  const text = raw.trim();
  if (text === "<<") throw new YamlError("Merge keys (<<) are not supported", lineNo);
  if (text.startsWith("? ")) throw new YamlError("Complex mapping keys (? ) are not supported", lineNo);
  if (text.startsWith('"') || text.startsWith("'")) return String(parseFlow(text, lineNo));
  if (text[0] === "&" || text[0] === "*") throw new YamlError("Anchors and aliases are not supported", lineNo);
  if (text.startsWith("!")) throw new YamlError("Explicit tags are not supported", lineNo);
  return text;
}

/**
 * Parse a YAML document into a JSON-compatible value.
 * @returns {{ ok: boolean, value: unknown, error: {message: string, line: number|null}|null }}
 */
export function fromYaml(text) {
  if (typeof text !== "string" || text.trim() === "") {
    return { ok: false, value: null, error: { message: "Input is empty", line: null } };
  }
  // Worked on a copy: `- key: value` is handled by rewriting the dash line.
  const lines = text.split(/\r?\n/).slice();
  let idx = 0;

  const isBlank = (i) => stripComment(lines[i] ?? "").trim() === "";

  const skipBlanks = () => {
    while (idx < lines.length && isBlank(idx)) idx += 1;
  };

  function readBlockScalar(header, parentIndent) {
    const style = header[0];
    const chomp = /[-+]/.exec(header)?.[0] ?? "";
    const explicit = /\d/.exec(header)?.[0];
    idx += 1;
    const collected = [];
    let blockIndent = explicit ? parentIndent + Number(explicit) : null;
    while (idx < lines.length) {
      const raw = lines[idx];
      if (raw.trim() === "") {
        collected.push("");
        idx += 1;
        continue;
      }
      const ind = indentOf(raw);
      if (ind <= parentIndent) break;
      if (blockIndent === null) blockIndent = ind;
      if (ind < blockIndent) break;
      collected.push(raw.slice(blockIndent));
      idx += 1;
    }
    while (collected.length && collected[collected.length - 1] === "") collected.pop();
    let body;
    if (style === "|") {
      body = collected.join("\n");
    } else {
      // Folded: lines join with a space, and a run of N blank lines becomes N
      // newlines — not N+1. The blank replaces the fold rather than adding to it.
      let out = "";
      let blanks = 0;
      let first = true;
      for (const line of collected) {
        if (line === "") {
          blanks += 1;
          continue;
        }
        if (first) {
          out = line;
          first = false;
        } else if (blanks > 0) {
          out += "\n".repeat(blanks) + line;
        } else {
          out += ` ${line}`;
        }
        blanks = 0;
      }
      body = out;
    }
    if (chomp === "-") return body;
    if (chomp === "+") return `${body}\n`;
    return body === "" ? "" : `${body}\n`;
  }

  function parseNode(minIndent) {
    skipBlanks();
    if (idx >= lines.length) return null;
    const ind = indentOf(lines[idx]);
    if (ind < minIndent) return null;
    const content = stripComment(lines[idx]).trim();
    if (content === "-" || content.startsWith("- ")) return parseSequence(ind);
    return parseMapping(ind);
  }

  function parseSequence(ind) {
    const out = [];
    for (;;) {
      skipBlanks();
      if (idx >= lines.length) break;
      const raw = lines[idx];
      if (indentOf(raw) !== ind) break;
      const content = stripComment(raw).trim();
      if (content !== "-" && !content.startsWith("- ")) break;
      const rest = content === "-" ? "" : content.slice(2).trim();
      const lineNo = idx + 1;

      if (rest === "") {
        idx += 1;
        out.push(parseNode(ind + 1));
        continue;
      }
      if (/^[|>]/.test(rest)) {
        out.push(readBlockScalar(rest, ind));
        continue;
      }
      // `- key: value` — re-indent the remainder and parse it as a mapping.
      const dashOffset = raw.indexOf("-") + 2;
      if (findKeyColon(rest) !== -1 && !"[{\"'".includes(rest[0])) {
        lines[idx] = " ".repeat(dashOffset) + rest;
        out.push(parseMapping(dashOffset));
        continue;
      }
      out.push(parseScalarText(rest, lineNo));
      idx += 1;
    }
    return out;
  }

  function parseMapping(ind) {
    const out = {};
    for (;;) {
      skipBlanks();
      if (idx >= lines.length) break;
      const raw = lines[idx];
      if (indentOf(raw) !== ind) break;
      const content = stripComment(raw).trim();
      if (content === "" || content === "---" || content === "...") break;
      if (content === "-" || content.startsWith("- ")) break;

      const lineNo = idx + 1;
      const colon = findKeyColon(content);
      if (colon === -1) throw new YamlError(`Expected 'key: value' but found ${JSON.stringify(content)}`, lineNo);
      const key = decodeKey(content.slice(0, colon), lineNo);
      const rest = content.slice(colon + 1).trim();

      if (rest === "") {
        idx += 1;
        skipBlanks();
        const childIndent = idx < lines.length ? indentOf(lines[idx]) : -1;
        const childContent = idx < lines.length ? stripComment(lines[idx]).trim() : "";
        if (childIndent > ind) {
          out[key] = parseNode(childIndent);
        } else if (childIndent === ind && (childContent === "-" || childContent.startsWith("- "))) {
          // A sequence may sit at the same indent as the key that owns it.
          out[key] = parseSequence(ind);
        } else {
          out[key] = null;
        }
        continue;
      }
      if (/^[|>]/.test(rest)) {
        out[key] = readBlockScalar(rest, ind);
        continue;
      }
      out[key] = parseScalarText(rest, lineNo);
      idx += 1;
    }
    return out;
  }

  try {
    skipBlanks();
    // A leading `---` is fine; a second one means a multi-document stream.
    if (idx < lines.length && stripComment(lines[idx]).trim() === "---") idx += 1;
    for (let scan = idx; scan < lines.length; scan += 1) {
      if (stripComment(lines[scan]).trim() === "---") {
        return {
          ok: false,
          value: null,
          error: { message: "Multi-document streams are not supported — one document per conversion", line: scan + 1 },
        };
      }
    }
    return { ok: true, value: parseNode(0), error: null };
  } catch (error) {
    if (!(error instanceof YamlError)) throw error;
    return { ok: false, value: null, error: { message: error.message, line: error.line } };
  }
}
